import { useCallback, useSyncExternalStore } from "react";

const POLL_MS = 1000;

// One shared clipboard store for the whole app: a single interval polls the
// clipboard while at least one component reads it, instead of one per hook call.
let value;
let timer = null;
const listeners = new Set();

function canRead() {
  return typeof navigator !== "undefined" && !!navigator.clipboard?.readText;
}

function canWrite() {
  return typeof navigator !== "undefined" && !!navigator.clipboard?.writeText;
}

function setValue(next) {
  if (next === value) return;
  value = next;
  listeners.forEach((listener) => listener());
}

function tick() {
  // readText is rejected without focus anyway: don't even ask
  if (document.visibilityState !== "visible" || !document.hasFocus()) return;
  navigator.clipboard
    .readText()
    .then(setValue)
    .catch(() => {});
}

function subscribe(listener) {
  listeners.add(listener);
  if (!timer && canRead()) {
    timer = setInterval(tick, POLL_MS);
    // The value may be stale since the last reader left: read it now.
    tick();
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
}

function getSnapshot() {
  return value;
}

function copyText(text, onCopied) {
  // No string check: callers pass ids that may not be typed as strings, and
  // writeText coerces them (the old check was a no-op)
  if (!canWrite()) return;
  navigator.clipboard
    .writeText(text)
    .then(() => {
      setValue(text);
      onCopied && onCopied(text);
    })
    .catch((e) => console.log(e));
}

// Reads the clipboard (subscribes to the shared poll) and can copy.
export default function useClipboard({ onCopied } = {}) {
  const clipboard = useSyncExternalStore(subscribe, getSnapshot);
  const { copy } = useClipboardCopy({ onCopied });

  return { clipboard, copy };
}

// Copy only: never subscribes, so it never starts the poll.
export function useClipboardCopy({ onCopied } = {}) {
  const copy = useCallback((text) => copyText(text, onCopied), [onCopied]);

  return { copy };
}

// Internal: exposed for tests only.
export const __clipboardStore = {
  subscribe,
  getSnapshot,
  copyText,
  reset() {
    listeners.clear();
    if (timer) clearInterval(timer);
    timer = null;
    value = undefined;
  },
  isPolling: () => timer !== null
};
